import { LockOutlined, SafetyCertificateOutlined, UserOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Form, Input, Typography } from "antd";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { login } from "../api/auth";
import { ApiError } from "../api/client";
import { BrandIdentity } from "../components/BrandIdentity";
import { RegistryMascot } from "../components/RegistryMascot";
import { useAuth } from "../features/auth/AuthContext";
import { getDefaultPathForRole } from "../features/shell/navigation";

interface LoginValues {
  username: string;
  password: string;
}

export function LoginPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const mutation = useMutation({
    mutationFn: ({ username, password }: LoginValues) => login(username, password),
    onSuccess: async ({ user }) => {
      queryClient.setQueryData(["current-user"], user);
      const requestedPath = (location.state as { from?: string } | null)?.from;
      const destination =
        requestedPath && requestedPath !== "/" ? requestedPath : getDefaultPathForRole(user.role);
      await navigate(destination, { replace: true });
    },
  });

  if (!auth.isLoading && auth.user) {
    return <Navigate to={getDefaultPathForRole(auth.user.role)} replace />;
  }
  const errorMessage =
    mutation.error instanceof ApiError ? mutation.error.message : "暂时无法登录，请稍后重试";

  return (
    <main className="login-page">
      <section className="login-story" aria-label="系统品牌与动态吉祥物">
        <div className="login-brand">
          <BrandIdentity inverse />
        </div>
        <RegistryMascot />
      </section>

      <section className="login-form-panel">
        <div className="login-form-wrap">
          <Typography.Text className="section-eyebrow">ACCOUNT ACCESS</Typography.Text>
          <Typography.Title level={1}>登录系统</Typography.Title>
          <Typography.Paragraph type="secondary">
            请输入管理员分配的独立用户名和密码。
          </Typography.Paragraph>
          {mutation.isError ? <Alert showIcon type="error" title={errorMessage} /> : null}
          <Form<LoginValues>
            layout="vertical"
            size="large"
            requiredMark={false}
            onFinish={(values) => mutation.mutate(values)}
          >
            <Form.Item
              name="username"
              label="用户名"
              rules={[{ required: true, message: "请输入用户名" }]}
            >
              <Input
                prefix={<UserOutlined />}
                autoComplete="username"
                placeholder="请输入用户名"
                autoFocus
              />
            </Form.Item>
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: "请输入密码" }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                autoComplete="current-password"
                placeholder="请输入密码"
              />
            </Form.Item>
            <Button block type="primary" htmlType="submit" loading={mutation.isPending}>
              安全登录
            </Button>
          </Form>
          <div className="login-security-note">
            <SafetyCertificateOutlined /> 登录过程采用安全 Cookie，会话操作均写入审计日志。
          </div>
        </div>
      </section>
    </main>
  );
}
